/**
 * Build-readiness evaluation for a workload (NC-983 / F1).
 *
 * A tutor can currently send a half-configured workload into a 60–90 min /
 * €5–10 image build and only learn it was wrong ~an hour later as a Failed
 * status. This helper derives — purely from the workload's own fields, no
 * backend call — whether the essentials for a build are in place, so the
 * editor can show a checklist and warn BEFORE dispatch.
 *
 * Scope note: installer-blob presence is intentionally NOT checked here — that
 * is the live, backend-gated concern of the NC-967 readiness endpoint used by
 * the NC-972 wizard. This helper covers the static, editor-local essentials
 * and is reused as the wizard's "Readiness" step logic.
 */

export interface ReadinessCheck {
  key: string;
  label: string;
  ok: boolean;
  /** Short actionable hint shown when the check is not satisfied. */
  hint: string;
}

export interface WorkloadReadinessInput {
  applicationsCount: number;
  baseImage: string;
  templateFile: string;
  variablesFile: string;
}

export interface WorkloadReadinessResult {
  checks: ReadinessCheck[];
  allReady: boolean;
  /** Labels of the checks that are not yet satisfied (for pre-dispatch warnings). */
  openLabels: string[];
}

const nonEmpty = (value: string | undefined | null): boolean =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Evaluate whether a workload is ready for an image build. Pure + deterministic
 * so it can be unit-tested and reused by the wizard without a render.
 */
export function evaluateBuildReadiness(input: WorkloadReadinessInput): WorkloadReadinessResult {
  const checks: ReadinessCheck[] = [
    {
      key: 'applications',
      label: 'Mindestens eine Anwendung definiert',
      ok: input.applicationsCount >= 1,
      hint: 'Füge unter „Anwendungen" mindestens eine Applikation hinzu.',
    },
    {
      key: 'baseImage',
      label: 'Basis-Image gesetzt',
      ok: nonEmpty(input.baseImage),
      hint: 'Trage in der Packer-Konfiguration ein Basis-Image ein (z. B. „Win11-23H2-Enterprise").',
    },
    {
      key: 'templateFile',
      label: 'Packer-Template-Datei gesetzt',
      ok: nonEmpty(input.templateFile),
      hint: 'Trage die Template-Datei ein (z. B. „cam-hypermill.pkr.hcl").',
    },
    {
      key: 'variablesFile',
      label: 'Packer-Variablen-Datei gesetzt',
      ok: nonEmpty(input.variablesFile),
      hint: 'Trage die Variablen-Datei ein (z. B. „variables/cam.pkrvars.hcl").',
    },
  ];

  const openLabels = checks.filter((c) => !c.ok).map((c) => c.label);

  return {
    checks,
    allReady: openLabels.length === 0,
    openLabels,
  };
}
